import {
  ActionCreatorWithPayload,
  createSelector,
  createSlice,
  Dictionary,
} from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';
import type { AppDispatch, RootState } from '../store';

import { DEFAULT_PAGE_SIZE } from '../constants';
import type { PagedList } from '../models/paged-list.model';
import type { BaseEvent } from '../models/events/_BaseEvent';
import type { TripLegSearchRequest } from '../requests/trip-legs';

import { tripLegAdapter } from '../entities/trip-leg.entity';
import triplegService from '../../services/triplegService';
import * as uiSlice from '../ui.slice';
import { isAbortedRequestError } from '../../services/httpService.utils';
import { TripLegSearchResponseItem } from '../models/trip-leg-search.model';
import { toTripLegStatusBC } from '../models/trip-leg-status.model';
import { TransportTripLeg } from '../models/transport-reservation.model';
import { Driver } from '../models/driver.model';
import { driverSlice, selectDriverEntities } from './driver.slice';

const DEFAULT_PAGED_LIST: PagedList<string> = {
  currentPage: 1,
  totalPages: 0,
  itemCount: 0,
  filters: {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: 'pickUpDueTimeUTC',
    sortOrder: 'desc',
  },
  items: [],
};

type TripLegEntityState = {
  // The tripLegEventQueue is used to track incoming events so data fetches can
  // be debounced or the events processed in a specific order.
  tripLegEventQueue: Partial<BaseEvent>[];
  tripManagementTodaysTrips: PagedList<string>;
  loadingTripManagementTodaysTrips: boolean;
  loadedTripManagementTodaysTrips: boolean;
  tripManagementAllTrips: PagedList<string>;
  loadingTripManagementAllTrips: boolean;
  loadedTripManagementAllTrips: boolean;
  tripManagementDiscussTrips: PagedList<string>;
  loadingTripManagementDiscussTrips: boolean;
  loadedTripManagementDiscussTrips: boolean;
  monitoringTodaysTrips: PagedList<string>;
  loadingMonitoringTodaysTrips: boolean;
  loadedMonitoringTodaysTrips: boolean;
  monitoringExceptions: PagedList<string>;
  loadingMonitoringExceptions: boolean;
  loadedMonitoringExceptions: boolean;
};

// Websocket event payloads carry a trip leg's status as a lowercase `status`
// (e.g. 'canceled'), while search result items and every table render
// `tripLegStatusBC` (e.g. 'CANCELED'). Normalizing it here keeps already
// loaded rows' statuses current on every page without a refetch.
const withNormalizedStatus = (
  tripLeg: TripLegSearchResponseItem,
): TripLegSearchResponseItem => {
  const { status } = tripLeg as { status?: string };
  if (!status || tripLeg.tripLegStatusBC) {
    return tripLeg;
  }
  return { ...tripLeg, tripLegStatusBC: toTripLegStatusBC(status) };
};

const tripLegSlice = createSlice({
  name: 'trip-leg',
  initialState: tripLegAdapter.getInitialState<TripLegEntityState>({
    tripLegEventQueue: [],
    tripManagementTodaysTrips: { ...DEFAULT_PAGED_LIST },
    loadingTripManagementTodaysTrips: false,
    loadedTripManagementTodaysTrips: false,
    tripManagementAllTrips: { ...DEFAULT_PAGED_LIST },
    loadingTripManagementAllTrips: false,
    loadedTripManagementAllTrips: false,
    tripManagementDiscussTrips: { ...DEFAULT_PAGED_LIST },
    loadingTripManagementDiscussTrips: false,
    loadedTripManagementDiscussTrips: false,
    monitoringTodaysTrips: { ...DEFAULT_PAGED_LIST },
    loadingMonitoringTodaysTrips: false,
    loadedMonitoringTodaysTrips: false,
    monitoringExceptions: { ...DEFAULT_PAGED_LIST },
    loadingMonitoringExceptions: false,
    loadedMonitoringExceptions: false,
  }),
  reducers: {
    // Trip leg entity related actions
    setTripLegs: (
      state,
      action: { payload: PagedList<TripLegSearchResponseItem> },
    ) => {
      tripLegAdapter.setMany(state, action.payload.items);
    },
    upsertTripLegsFromTransportReservation: (
      state,
      action: { payload: TransportTripLeg[] },
    ) => {
      tripLegAdapter.upsertMany(
        state,
        action.payload as unknown as TripLegSearchResponseItem[],
      );
    },

    // Trip leg websocket event related actions
    // A drafted or opened event can be a new trip leg or an existing one
    // changing status (e.g. draft or on hold to open). Its payload uses the
    // event's shape rather than the search result's, so like tripLegUpdated
    // below, only an already loaded trip leg is updated. A new one is left
    // out of the store until a page's fetch loads it in full, since an IDs-
    // only sync never hydrates an ID that's already present.
    tripLegOpened: (state, action: { payload: TripLegSearchResponseItem }) => {
      tripLegAdapter.updateOne(state, {
        id: action.payload.tripLegReferenceID,
        changes: withNormalizedStatus(action.payload),
      });
    },
    // Websocket event payloads for generic status transitions are lean (e.g.
    // just tripLegReferenceID/status), not full search result items. Using
    // updateOne instead of upsertOne means a trip leg we haven't fully loaded
    // yet is left alone here (a no-op) rather than being inserted as a broken
    // partial entity that then blocks the syncIdsOnly hydration fetch, since
    // that fetch treats any already-present id as already loaded.
    tripLegUpdated: (state, action: { payload: TripLegSearchResponseItem }) => {
      tripLegAdapter.updateOne(state, {
        id: action.payload.tripLegReferenceID!,
        changes: withNormalizedStatus(action.payload),
      });
    },
    // Driver assignment payloads are lean too (IDs plus driver details), so
    // the same updateOne reasoning as tripLegUpdated applies.
    tripLegDriverAssigned: (
      state,
      action: { payload: TripLegSearchResponseItem },
    ) => {
      tripLegAdapter.updateOne(state, {
        id: action.payload.tripLegReferenceID,
        changes: action.payload,
      });
    },
    tripLegExceptionCleared: (
      state,
      action: { payload: TripLegSearchResponseItem },
    ) => {
      tripLegAdapter.updateOne(state, {
        id: action.payload.tripLegReferenceID!,
        changes: {
          isException: false,
          tripLegExceptionBC: null,
        },
      });
    },
    tripLegEscalated: (
      state,
      action: { payload: TripLegSearchResponseItem },
    ) => {
      tripLegAdapter.updateOne(state, {
        id: action.payload.tripLegReferenceID!,
        changes: {
          isEscalated: true,
        },
      });
    },
    // "Activating" a will call means it's being converted into a scheduled
    // trip with a concrete pickup/appointment time (see activateWillCall in
    // ReservationAccordion.tsx, which sets isWillCall: false alongside the
    // new times), so this event moves a trip leg OUT of will-call status,
    // not into it. That function sets the new time under the TripLeg
    // (form/edit) field names appointmentDateTime/pickUpDueDateTime, which
    // are the timestamp counterparts of this search-projection entity's
    // appointmentTimeUTC/pickUpDueTimeUTC -- NOT the date-only
    // appointmentDate field (transportReservationMapper.ts derives
    // appointmentDateTime from appointmentTimeUTC and passes appointmentDate
    // through separately/unchanged). Map them so every consumer of this
    // shared entity (MonitoringExceptions, TodaysTrips, Discussions,
    // MonitorTrips) renders and sorts the activated trip leg correctly, not
    // just whichever page happens to force its own refetch afterward. Still
    // merge the rest of the payload like the default tripLegUpdated case
    // does (including whatever appointmentDate the event itself carries),
    // and force isWillCall: false in case the payload doesn't restate it.
    tripLegWillCallActivated: (
      state,
      action: {
        payload: TripLegSearchResponseItem & {
          appointmentDateTime?: string;
          pickUpDueDateTime?: string;
        };
      },
    ) => {
      const { appointmentDateTime, pickUpDueDateTime, ...payload } =
        action.payload;
      tripLegAdapter.updateOne(state, {
        id: payload.tripLegReferenceID!,
        changes: {
          ...payload,
          isWillCall: false,
          ...(appointmentDateTime && {
            appointmentTimeUTC: appointmentDateTime,
          }),
          ...(pickUpDueDateTime && { pickUpDueTimeUTC: pickUpDueDateTime }),
        },
      });
    },

    // Event queue related actions
    addToTripLegEventQueue: (
      state,
      action: { payload: Partial<BaseEvent> },
    ) => {
      state.tripLegEventQueue = [...state.tripLegEventQueue, action.payload];
    },
    // Takes the length of the queue snapshot the caller actually processed
    // (i.e. tripLegEventQueue.length as read by their effect), not an event
    // identity. Identity-based matching (detailType + tripLegReferenceID) is
    // ambiguous when duplicates are queued -- e.g. a redelivered websocket
    // message -- and more importantly, this reducer always runs against the
    // CURRENT queue, which can have grown since the caller's snapshot was
    // captured: a render and this effect's dispatch aren't atomic, so a new
    // websocket delivery can append an event in that gap. Matching by
    // identity in that case can select the new entry instead of the one the
    // caller actually processed, discarding it unprocessed. Slicing by the
    // snapshot's length instead always drops exactly what was processed and
    // preserves anything appended after, regardless of identity collisions.
    removeTripLegEventFromQueue: (state, action: { payload: number }) => {
      state.tripLegEventQueue = state.tripLegEventQueue.slice(action.payload);
    },

    // Page specific setters
    setTripManagementTodaysTrips: (
      state,
      action: { payload: PagedList<TripLegSearchResponseItem> },
    ) => {
      const newState = {
        ...state.tripManagementTodaysTrips,
        ...action.payload,
        items: action.payload.items.map((item) => item.tripLegReferenceID),
      };
      state.tripManagementTodaysTrips = newState;
    },
    setLoadingTripManagementTodaysTrips: (
      state,
      action: { payload: boolean },
    ) => {
      state.loadingTripManagementTodaysTrips = action.payload;
    },
    setLoadedTripManagementTodaysTrips: (
      state,
      action: { payload: boolean },
    ) => {
      state.loadedTripManagementTodaysTrips = action.payload;
    },
    setTripManagementAllTrips: (
      state,
      action: { payload: PagedList<TripLegSearchResponseItem> },
    ) => {
      state.tripManagementAllTrips = {
        ...state.tripManagementAllTrips,
        ...action.payload,
        items: action.payload.items.map((item) => item.tripLegReferenceID),
      };
    },
    setLoadingTripManagementAllTrips: (state, action: { payload: boolean }) => {
      state.loadingTripManagementAllTrips = action.payload;
    },
    setLoadedTripManagementAllTrips: (state, action: { payload: boolean }) => {
      state.loadedTripManagementAllTrips = action.payload;
    },
    setTripManagementDiscussTrips: (
      state,
      action: { payload: PagedList<TripLegSearchResponseItem> },
    ) => {
      state.tripManagementDiscussTrips = {
        ...state.tripManagementDiscussTrips,
        ...action.payload,
        items: action.payload.items.map((item) => item.tripLegReferenceID),
      };
    },
    setLoadingTripManagementDiscussTrips: (
      state,
      action: { payload: boolean },
    ) => {
      state.loadingTripManagementDiscussTrips = action.payload;
    },
    setLoadedTripManagementDiscussTrips: (
      state,
      action: { payload: boolean },
    ) => {
      state.loadedTripManagementDiscussTrips = action.payload;
    },
    removeTripManagementDiscussTrip: (state, action: { payload: string }) => {
      const items = state.tripManagementDiscussTrips.items.filter(
        (id) => id !== action.payload,
      );

      if (items.length === state.tripManagementDiscussTrips.items.length) {
        //exit if item is not on the list or was already removed
        return;
      }
      state.tripManagementDiscussTrips = {
        ...state.tripManagementDiscussTrips,
        items,
        itemCount: Math.max(0, state.tripManagementDiscussTrips.itemCount - 1),
      };
    },
    setMonitoringTodaysTrips: (
      state,
      action: { payload: PagedList<TripLegSearchResponseItem> },
    ) => {
      state.monitoringTodaysTrips = {
        ...state.monitoringTodaysTrips,
        ...action.payload,
        items: action.payload.items.map((item) => item.tripLegReferenceID),
      };
    },
    setLoadingMonitoringTodaysTrips: (state, action: { payload: boolean }) => {
      state.loadingMonitoringTodaysTrips = action.payload;
    },
    setLoadedMonitoringTodaysTrips: (state, action: { payload: boolean }) => {
      state.loadedMonitoringTodaysTrips = action.payload;
    },
    setMonitoringExceptions: (
      state,
      action: { payload: PagedList<TripLegSearchResponseItem> },
    ) => {
      state.monitoringExceptions = {
        ...state.monitoringExceptions,
        ...action.payload,
        items: action.payload.items.map((item) => item.tripLegReferenceID),
      };
    },
    setLoadingMonitoringExceptions: (state, action: { payload: boolean }) => {
      state.loadingMonitoringExceptions = action.payload;
    },
    setLoadedMonitoringExceptions: (state, action: { payload: boolean }) => {
      state.loadedMonitoringExceptions = action.payload;
    },
    // Mirrors removeTripManagementDiscussTrip: drops an id from the page's
    // list immediately (no network round trip), so a cleared exception's row
    // disappears in one step instead of visually clearing first and then
    // vanishing once a refetch completes.
    removeMonitoringExceptionsItem: (state, action: { payload: string }) => {
      const items = state.monitoringExceptions.items.filter(
        (id) => id !== action.payload,
      );
      if (items.length === state.monitoringExceptions.items.length) {
        // exit if item is not on the list or was already removed
        return;
      }
      state.monitoringExceptions = {
        ...state.monitoringExceptions,
        items,
        itemCount: Math.max(0, state.monitoringExceptions.itemCount - 1),
      };
    },
  },
});

const {
  setTripLegs,
  upsertTripLegsFromTransportReservation,
  tripLegOpened,
  tripLegUpdated,
  tripLegDriverAssigned,
  tripLegExceptionCleared,
  tripLegEscalated,
  tripLegWillCallActivated,
  addToTripLegEventQueue,
  removeTripLegEventFromQueue,
  setTripManagementTodaysTrips,
  setLoadingTripManagementTodaysTrips,
  setLoadedTripManagementTodaysTrips,
  setTripManagementAllTrips,
  setLoadingTripManagementAllTrips,
  setLoadedTripManagementAllTrips,
  setTripManagementDiscussTrips,
  setLoadingTripManagementDiscussTrips,
  setLoadedTripManagementDiscussTrips,
  removeTripManagementDiscussTrip,
  setMonitoringTodaysTrips,
  setLoadingMonitoringTodaysTrips,
  setLoadedMonitoringTodaysTrips,
  setMonitoringExceptions,
  setLoadingMonitoringExceptions,
  setLoadedMonitoringExceptions,
  removeMonitoringExceptionsItem,
} = tripLegSlice.actions;

const getFetchTripLegsSearchAsyncFunction = ({
  setDataAction,
  setLoadingAction,
  setLoadedAction,
}: {
  setDataAction?: ActionCreatorWithPayload<
    PagedList<TripLegSearchResponseItem>
  >;
  setLoadingAction?: ActionCreatorWithPayload<boolean>;
  setLoadedAction?: ActionCreatorWithPayload<boolean>;
}) => {
  return ({
    signal,
    syncIdsOnly,
    ...request
  }: TripLegSearchRequest & {
    signal?: AbortSignal;
    // When true, only reference IDs are requested from the backend to sync
    // the page's ID list. The entity adapter is then hydrated by fetching
    // full trip legs for whichever of those reference IDs that are missing.
    syncIdsOnly?: boolean;
  }) => {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
      if (setLoadingAction) dispatch(setLoadingAction(true));
      if (setLoadedAction) dispatch(setLoadedAction(false));

      const searchRequest = syncIdsOnly
        ? { ...request, select: ['tripLegReferenceID'] }
        : request;

      triplegService.getTripLegsSearch(searchRequest, signal).subscribe({
        next: (response) => {
          if (signal?.aborted) return;

          if (syncIdsOnly) {
            const loadedIds = tripLegSelectors.selectIds(getState());
            const missingTripLegReferenceIDs = response.items
              .map((item) => item.tripLegReferenceID)
              .filter((id) => !!id && !loadedIds.includes(id));
            if (missingTripLegReferenceIDs.length) {
              dispatch(
                fetchTripsByReferenceIdsAsync({
                  // pass through auth related fields
                  passengerUUID: searchRequest?.passengerUUID,
                  clientBC: searchRequest?.clientBC,
                  clientPassengerID: searchRequest?.clientPassengerID,
                  // search missing ids
                  tripLegReferenceID: missingTripLegReferenceIDs,
                  signal,
                }),
              );
            }
          } else {
            // Keep the driver entity slice as the source of truth for any
            // driver data the search projection includes
            dispatch(
              driverSlice.actions.upsertDrivers(
                (response.items as TripLegEntity[]).map((item) => item.driver),
              ),
            );
            dispatch(setTripLegs(response));
          }

          if (setDataAction) dispatch(setDataAction(response));
          if (setLoadingAction) dispatch(setLoadingAction(false));
          if (setLoadedAction) dispatch(setLoadedAction(true));
        },
        error: (error) => {
          if (isAbortedRequestError(error) || signal?.aborted) return;

          dispatch(uiSlice.setErrorMessage(error.message));
          if (setLoadingAction) dispatch(setLoadingAction(false));
          if (setLoadedAction) dispatch(setLoadedAction(false));
        },
      });
    };
  };
};

// Fetches full trip legs (not just reference IDs) for the given reference
// IDs, regardless of whether they're already loaded. Used internally to
// hydrate the entity adapter for ids returned from syncIdsOnly requests, and
// exported for callers that need to force-refresh an already-loaded entity's
// fields from a realtime event whose payload may not match the search
// projection's shape (syncIdsOnly skips ids already in the adapter, so it
// can't be used for that).
const fetchTripsByReferenceIdsAsync = getFetchTripLegsSearchAsyncFunction({});

const fetchTripManagementTodaysTripsAsync = getFetchTripLegsSearchAsyncFunction(
  {
    setDataAction: setTripManagementTodaysTrips,
    setLoadingAction: setLoadingTripManagementTodaysTrips,
    setLoadedAction: setLoadedTripManagementTodaysTrips,
  },
);

const fetchTripManagementAllTripsAsync = getFetchTripLegsSearchAsyncFunction({
  setDataAction: setTripManagementAllTrips,
  setLoadingAction: setLoadingTripManagementAllTrips,
  setLoadedAction: setLoadedTripManagementAllTrips,
});

const fetchTripManagementDiscussTripsAsync =
  getFetchTripLegsSearchAsyncFunction({
    setDataAction: setTripManagementDiscussTrips,
    setLoadingAction: setLoadingTripManagementDiscussTrips,
    setLoadedAction: setLoadedTripManagementDiscussTrips,
  });

const fetchMonitoringTodaysTripsAsync = getFetchTripLegsSearchAsyncFunction({
  setDataAction: setMonitoringTodaysTrips,
  setLoadingAction: setLoadingMonitoringTodaysTrips,
  setLoadedAction: setLoadedMonitoringTodaysTrips,
});

const fetchMonitoringExceptionsAsync = getFetchTripLegsSearchAsyncFunction({
  setDataAction: setMonitoringExceptions,
  setLoadingAction: setLoadingMonitoringExceptions,
  setLoadedAction: setLoadedMonitoringExceptions,
});

/*
 * Selectors
 * All necessary selectors for filtering and sorting trip legs for various
 * pages, including trip management and monitoring.
 */

const tripLegSelectors = tripLegAdapter.getSelectors<RootState>(
  (state) => state.tripLegV2,
);

type TripLegEntity = ReturnType<typeof tripLegSelectors.selectAll>[number];

// Trip legs embed a copy of their driver, which goes stale as soon as the
// driver is updated elsewhere. Overlay the driver entity slice's copy on the
// embedded one (the slice may only hold a partial driver from a lean websocket
// payload), and drop an embedded driver left over from a previous assignment.
// Results are cached per trip leg/driver pair so unchanged trip legs keep
// their identity and don't re-render consumers when an unrelated driver
// changes.
const mergedTripLegCache = new WeakMap<
  TripLegEntity,
  { driverEntity: Driver | undefined; merged: TripLegEntity }
>();

const mergeTripLegWithDriver = (
  tripLeg: TripLegEntity,
  drivers: Dictionary<Driver>,
): TripLegEntity => {
  // Without a driverID there's nothing to reconcile against
  if (tripLeg.driverID === undefined) return tripLeg;

  const embeddedDriver =
    tripLeg.driver?.driverID === tripLeg.driverID ? tripLeg.driver : undefined;
  const driverEntity =
    tripLeg.driverID === null ? undefined : drivers[tripLeg.driverID];

  if (driverEntity === tripLeg.driver) return tripLeg;
  if (!driverEntity && (!tripLeg.driver || embeddedDriver)) return tripLeg;

  const cached = mergedTripLegCache.get(tripLeg);
  if (cached && cached.driverEntity === driverEntity) return cached.merged;

  const driver =
    driverEntity && embeddedDriver
      ? { ...embeddedDriver, ...driverEntity }
      : (driverEntity ?? embeddedDriver);
  const merged = { ...tripLeg, driver };
  mergedTripLegCache.set(tripLeg, { driverEntity, merged });
  return merged;
};

const selectTripLegById = (state: RootState, tripLegReferenceID: string) => {
  const tripLeg = tripLegSelectors.selectById(state, tripLegReferenceID);
  return tripLeg
    ? mergeTripLegWithDriver(tripLeg, selectDriverEntities(state))
    : undefined;
};

const selectAllTripLegs = createSelector(
  [tripLegSelectors.selectAll, selectDriverEntities],
  (tripLegs, drivers) =>
    tripLegs.map((tripLeg) => mergeTripLegWithDriver(tripLeg, drivers)),
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectTripLegEventQueue = (state: RootState) =>
  state.tripLegV2.tripLegEventQueue;

// Selectors for loading and loaded boolean states
const selectLoadingTripManagementTodaysTrips = (state: RootState) =>
  state.tripLegV2.loadingTripManagementTodaysTrips;
const selectLoadedTripManagementTodaysTrips = (state: RootState) =>
  state.tripLegV2.loadedTripManagementTodaysTrips;
const selectLoadingTripManagementAllTrips = (state: RootState) =>
  state.tripLegV2.loadingTripManagementAllTrips;
const selectLoadedTripManagementAllTrips = (state: RootState) =>
  state.tripLegV2.loadedTripManagementAllTrips;
const selectLoadingTripManagementDiscussTrips = (state: RootState) =>
  state.tripLegV2.loadingTripManagementDiscussTrips;
const selectLoadedTripManagementDiscussTrips = (state: RootState) =>
  state.tripLegV2.loadedTripManagementDiscussTrips;
const selectLoadingMonitoringTodaysTrips = (state: RootState) =>
  state.tripLegV2.loadingMonitoringTodaysTrips;
const selectLoadedMonitoringTodaysTrips = (state: RootState) =>
  state.tripLegV2.loadedMonitoringTodaysTrips;
const selectLoadingMonitoringExceptions = (state: RootState) =>
  state.tripLegV2.loadingMonitoringExceptions;
const selectLoadedMonitoringExceptions = (state: RootState) =>
  state.tripLegV2.loadedMonitoringExceptions;

// Selectors for each view's state
const selectTripManagementTodaysTripsState = (state: RootState) =>
  state.tripLegV2.tripManagementTodaysTrips;
const selectTripManagementAllTripsState = (state: RootState) =>
  state.tripLegV2.tripManagementAllTrips;
const selectTripManagementDiscussTripsState = (state: RootState) =>
  state.tripLegV2.tripManagementDiscussTrips;
const selectMonitoringTodaysTripsState = (state: RootState) =>
  state.tripLegV2.monitoringTodaysTrips;
const selectMonitoringExceptionsState = (state: RootState) =>
  state.tripLegV2.monitoringExceptions;

// Selectors for IDs retrieved from API requests
const selectTripManagementTodaysTripsIDs = createSelector(
  [selectAllTripLegs, selectTripManagementTodaysTripsState],
  (entities, tripManagementTodaysTripsState) => {
    const ids = entities.map(
      (entity: TripLegSearchResponseItem) => entity.tripLegReferenceID,
    );
    return tripManagementTodaysTripsState.items.filter((id) =>
      ids.includes(id),
    );
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectTripManagementAllTripsIDs = createSelector(
  [selectAllTripLegs, selectTripManagementAllTripsState],
  (entities, tripManagementAllTripsState) => {
    const ids = entities.map(
      (entity: TripLegSearchResponseItem) => entity.tripLegReferenceID,
    );
    return tripManagementAllTripsState.items.filter((id) => ids.includes(id));
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectTripManagementDiscussTripsIDs = createSelector(
  [selectAllTripLegs, selectTripManagementDiscussTripsState],
  (entities, tripManagementDiscussTripsState) => {
    const ids = entities.map(
      (entity: TripLegSearchResponseItem) => entity.tripLegReferenceID,
    );
    return tripManagementDiscussTripsState.items.filter((id) =>
      ids.includes(id),
    );
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectMonitoringTodaysTripsIDs = createSelector(
  [selectAllTripLegs, selectMonitoringTodaysTripsState],
  (entities, monitoringTodaysTripsState) => {
    const ids = entities.map(
      (entity: TripLegSearchResponseItem) => entity.tripLegReferenceID,
    );
    return monitoringTodaysTripsState.items.filter((id) => ids.includes(id));
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectMonitoringExceptionsIDs = createSelector(
  [selectAllTripLegs, selectMonitoringExceptionsState],
  (entities, monitoringExceptionsState) => {
    const ids = entities.map(
      (entity: TripLegSearchResponseItem) => entity.tripLegReferenceID,
    );
    return monitoringExceptionsState.items.filter((id) => ids.includes(id));
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

// Derived selectors for entities mapped to IDs retrieved from API requests,
// These are what should be used in the view
const selectTripManagementTodaysTrips = createSelector(
  [
    selectAllTripLegs,
    selectTripManagementTodaysTripsIDs,
    selectTripManagementTodaysTripsState,
  ],
  (entities, tripManagementTodaysTripsIDs, tripManagementTodaysTripsState) => {
    return {
      currentPage: tripManagementTodaysTripsState.currentPage,
      totalPages: tripManagementTodaysTripsState.totalPages,
      itemCount: tripManagementTodaysTripsState.itemCount,
      filters: tripManagementTodaysTripsState.filters,
      items: entities
        .filter((entity: TripLegSearchResponseItem) =>
          tripManagementTodaysTripsIDs.includes(entity.tripLegReferenceID),
        )
        .sort(
          (a, b) =>
            tripManagementTodaysTripsState.items.indexOf(a.tripLegReferenceID) -
            tripManagementTodaysTripsState.items.indexOf(b.tripLegReferenceID),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectTripManagementAllTrips = createSelector(
  [
    selectAllTripLegs,
    selectTripManagementAllTripsIDs,
    selectTripManagementAllTripsState,
  ],
  (entities, tripManagementAllTripsIDs, tripManagementAllTripsState) => {
    return {
      currentPage: tripManagementAllTripsState.currentPage,
      totalPages: tripManagementAllTripsState.totalPages,
      itemCount: tripManagementAllTripsState.itemCount,
      filters: tripManagementAllTripsState.filters,
      items: entities
        .filter((entity: TripLegSearchResponseItem) =>
          tripManagementAllTripsIDs.includes(entity.tripLegReferenceID),
        )
        .sort(
          (a, b) =>
            tripManagementAllTripsState.items.indexOf(a.tripLegReferenceID) -
            tripManagementAllTripsState.items.indexOf(b.tripLegReferenceID),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectTripManagementDiscussTrips = createSelector(
  [
    selectAllTripLegs,
    selectTripManagementDiscussTripsIDs,
    selectTripManagementDiscussTripsState,
  ],
  (
    entities,
    tripManagementDiscussTripsIDs,
    tripManagementDiscussTripsState,
  ) => {
    return {
      currentPage: tripManagementDiscussTripsState.currentPage,
      totalPages: tripManagementDiscussTripsState.totalPages,
      itemCount: tripManagementDiscussTripsState.itemCount,
      filters: tripManagementDiscussTripsState.filters,
      items: entities
        .filter((entity: TripLegSearchResponseItem) =>
          tripManagementDiscussTripsIDs.includes(entity.tripLegReferenceID),
        )
        .sort(
          (a, b) =>
            tripManagementDiscussTripsState.items.indexOf(
              a.tripLegReferenceID,
            ) -
            tripManagementDiscussTripsState.items.indexOf(b.tripLegReferenceID),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectMonitoringTodaysTrips = createSelector(
  [
    selectAllTripLegs,
    selectMonitoringTodaysTripsIDs,
    selectMonitoringTodaysTripsState,
  ],
  (entities, monitoringTodaysTripsIDs, monitoringTodaysTripsState) => {
    return {
      currentPage: monitoringTodaysTripsState.currentPage,
      totalPages: monitoringTodaysTripsState.totalPages,
      itemCount: monitoringTodaysTripsState.itemCount,
      filters: monitoringTodaysTripsState.filters,
      items: entities
        .filter((entity: TripLegSearchResponseItem) =>
          monitoringTodaysTripsIDs.includes(entity.tripLegReferenceID),
        )
        .sort(
          (a, b) =>
            monitoringTodaysTripsState.items.indexOf(a.tripLegReferenceID) -
            monitoringTodaysTripsState.items.indexOf(b.tripLegReferenceID),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectMonitoringExceptions = createSelector(
  [
    selectAllTripLegs,
    selectMonitoringExceptionsIDs,
    selectMonitoringExceptionsState,
  ],
  (entities, monitoringExceptionsIDs, monitoringExceptionsState) => {
    return {
      currentPage: monitoringExceptionsState.currentPage,
      totalPages: monitoringExceptionsState.totalPages,
      itemCount: monitoringExceptionsState.itemCount,
      filters: monitoringExceptionsState.filters,
      items: entities
        .filter((entity: TripLegSearchResponseItem) =>
          monitoringExceptionsIDs.includes(entity.tripLegReferenceID),
        )
        .sort(
          (a, b) =>
            monitoringExceptionsState.items.indexOf(a.tripLegReferenceID) -
            monitoringExceptionsState.items.indexOf(b.tripLegReferenceID),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

export {
  tripLegSlice,
  tripLegOpened,
  tripLegUpdated,
  tripLegDriverAssigned,
  tripLegExceptionCleared,
  tripLegEscalated,
  tripLegWillCallActivated,
  addToTripLegEventQueue,
  removeTripLegEventFromQueue,
  fetchTripManagementTodaysTripsAsync,
  fetchTripManagementAllTripsAsync,
  fetchTripManagementDiscussTripsAsync,
  fetchMonitoringTodaysTripsAsync,
  fetchMonitoringExceptionsAsync,
  fetchTripsByReferenceIdsAsync,
  selectTripLegById,
  selectAllTripLegs,
  selectTripLegEventQueue,
  selectTripManagementTodaysTrips,
  selectTripManagementAllTrips,
  selectTripManagementDiscussTrips,
  removeTripManagementDiscussTrip,
  selectMonitoringTodaysTrips,
  selectMonitoringExceptions,
  selectLoadingTripManagementTodaysTrips,
  selectLoadedTripManagementTodaysTrips,
  selectLoadingTripManagementAllTrips,
  selectLoadedTripManagementAllTrips,
  selectLoadedTripManagementDiscussTrips,
  selectLoadingTripManagementDiscussTrips,
  selectLoadingMonitoringTodaysTrips,
  selectLoadedMonitoringTodaysTrips,
  selectLoadingMonitoringExceptions,
  selectLoadedMonitoringExceptions,
  removeMonitoringExceptionsItem,
};
